import {
    AppUser,
    CountryCodesWithNames,
    EventrDate,
    IList,
    ProfileShareSection,
} from '@eventr/common'
import {
    ColoursObj,
    getFlagEmojiFromCountryCode,
    listEntry,
    MenuButton,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'

interface ProfileDetailsProps {
    user?: AppUser
    profilePage?: boolean
}

export const ProfileDetails: FC<ProfileDetailsProps> = ({
    user,
    profilePage = false,
}) => {
    const {
        profile,
        selectedOrg,
        fetchList,
        setProfileShareSections,
        getProfileShareSections,
    } = useBoundStore(
        useShallow((s) => {
            return {
                profile: s.profile,
                selectedOrg: s.selectedOrg,
                fetchList: s.fetchList,
                setProfileShareSections: s.setProfileShareSections,
                getProfileShareSections: s.getProfileShareSections,
            }
        })
    )

    const userData = profilePage ? profile : user

    const tagData = useMemo(() => {
        return profilePage
            ? profile?.organisations.find((s) => s.name === selectedOrg?.name)
            : user
    }, [user, profilePage, selectedOrg, profile?.organisations])

    const { getLanguageISO639Code } = useLanguageContext()

    const [fetchAddInfo, setfetchAddInfo] = useState<boolean>(false)

    useEffect(() => {
        const fetchInfo = () => {
            setProfileShareSections()
            setfetchAddInfo(true)
        }
        if (!fetchAddInfo) {
            fetchInfo()
        }
    }, [fetchAddInfo, setProfileShareSections])

    const sections: ProfileShareSection[] = (
        getProfileShareSections() ?? []
    ).filter((s) => s.enabled)

    const addInfo = profilePage ? profile?.additionalInfo : user?.additionalInfo

    const allFields = sections.reduce((acc, section) => {
        const sectionFields =
            section.fields
                ?.map((field) => {
                    const fieldValue = addInfo?.[section.id]?.[field.id]
                    if (fieldValue) {
                        return {
                            id: field.id,
                            fieldName: field.name,
                            value: fieldValue,
                            type: field.type,
                            list: field.listType
                                ? (
                                      fetchList(field.listType) as IList
                                  ).elements?.map((e) => ({
                                      id: e.id,
                                      displayName: e.name,
                                  })) ?? []
                                : [],
                        }
                    }
                    return null
                })
                ?.filter((field) => field !== null) ?? [] // Filter out null values

        if (sectionFields.length > 0) {
            acc[section.id] = sectionFields
        }

        return acc
    }, {} as Record<string, any[]>)

    return (
        <Stack gap="8px">
            {userData?.phoneNumber && (
                <ProfileFieldView
                    fieldName={i18n.t('account.profile.phoneNumber')}
                >
                    <Typography
                        variant="bodyRegular"
                        color={
                            userData?.phoneNumber
                                ? ColoursObj.font.primary
                                : ColoursObj.shades.shade2
                        }
                    >
                        {userData?.phoneNumber ??
                            i18n.t('account.profile.noPhoneNumber')}
                    </Typography>
                </ProfileFieldView>
            )}

            <ProfileFieldView fieldName={i18n.t('admin.email')}>
                <Typography
                    variant="bodyRegular"
                    id="userEmail"
                    color={ColoursObj.font.primary}
                >
                    {userData?.email || ''}
                </Typography>
            </ProfileFieldView>

            {userData?.countryCode && (
                <ProfileFieldView fieldName={i18n.t('admin.country')}>
                    <Typography
                        variant="bodyRegular"
                        id="userCountry"
                        color={ColoursObj.font.primary}
                    >
                        {getFlagEmojiFromCountryCode(
                            userData?.countryCode || ''
                        )}
                        {
                            CountryCodesWithNames.find((countryObj) => {
                                return countryObj.id === userData?.countryCode
                            })?.displayName
                        }
                    </Typography>
                </ProfileFieldView>
            )}
            {(tagData?.departmentTags?.length ?? 0) > 0 && (
                <ProfileFieldView fieldName={i18n.t('admin.department')}>
                    {(tagData?.departmentTags?.length ?? 0) > 0 ? (
                        <Box display="flex" sx={{ flexFlow: 'wrap' }}>
                            {tagData?.departmentTags?.map((dep, index) => (
                                <Fragment key={index}>
                                    <Typography
                                        variant="bodyRegular"
                                        color={ColoursObj.font.primary}
                                    >
                                        {dep || ''}
                                    </Typography>
                                    {index <
                                        (tagData?.departmentTags?.length ?? 0) -
                                            1 && (
                                        <Typography
                                            variant="bodyRegular"
                                            color={ColoursObj.font.primary}
                                            sx={{ marginRight: '8px' }}
                                        >
                                            ,
                                        </Typography>
                                    )}
                                </Fragment>
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="bodyRegular"
                            color={ColoursObj.shades.shade2}
                        >
                            {i18n.t('account.profile.noAssignedDepartments')}
                        </Typography>
                    )}
                </ProfileFieldView>
            )}
            {(tagData?.groupTags?.length ?? 0) > 0 && (
                <ProfileFieldView fieldName={i18n.t('admin.group')}>
                    {(tagData?.groupTags?.length ?? 0) > 0 ? (
                        <Box display="flex" sx={{ flexFlow: 'wrap' }}>
                            {tagData?.groupTags?.map((grp, index) => (
                                <Fragment key={index}>
                                    <Typography
                                        variant="bodyRegular"
                                        color={ColoursObj.font.primary}
                                    >
                                        {grp || ''}
                                    </Typography>
                                    {index <
                                        (tagData?.groupTags?.length ?? 0) -
                                            1 && (
                                        <Typography
                                            variant="bodyRegular"
                                            color={ColoursObj.font.primary}
                                            sx={{ marginRight: '8px' }}
                                        >
                                            ,
                                        </Typography>
                                    )}
                                </Fragment>
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="bodyRegular"
                            color={ColoursObj.shades.shade2}
                        >
                            {i18n.t('account.profile.noAssignedGroups')}
                        </Typography>
                    )}
                </ProfileFieldView>
            )}

            {user && (
                <Stack gap="8px">
                    {Object.entries(allFields).map(([sectionId, fields]) =>
                        fields.map((field) => (
                            <ProfileFieldView
                                key={`${sectionId}-${field.fieldName}`}
                                fieldName={i18n.t(
                                    `account.profile.additional-info.${sectionId}.fields.${field.id}`
                                )}
                            >
                                {profileMainFieldFormatter(
                                    field,
                                    getLanguageISO639Code()
                                )}
                            </ProfileFieldView>
                        ))
                    )}
                </Stack>
            )}
        </Stack>
    )
}

const getListDisplayName = (x: string, list: listEntry[]) => {
    const match = list?.find((element) => element.id === x)
    return match ? match.displayName : 'Unknown'
}

const ProfileFieldView = ({
    children,
    fieldName,
}: {
    children: React.ReactNode
    fieldName: string
}) => (
    <MenuButton
        style={{
            background: ColoursObj.backgrounds.secondary,
            padding: '8px 16px',
            height: 'max-content',
        }}
    >
        <Stack justifyContent="center" alignItems="flex-start">
            <Typography variant="bodySubtext" color={ColoursObj.font.primary}>
                {fieldName}
            </Typography>
            <Stack style={{ minHeight: '24px' }}>{children}</Stack>
        </Stack>
    </MenuButton>
)

const ListDisplay = ({
    addInfo,
    elements,
}: {
    addInfo: string[]
    elements: listEntry[]
}) => {
    const MAX_VISIBLE_ITEMS = 2
    const visibleItems = addInfo.slice(0, MAX_VISIBLE_ITEMS)
    const overflowCount = addInfo.length - MAX_VISIBLE_ITEMS

    return (
        <Stack direction="row" flexWrap="wrap" gap="4px">
            {visibleItems.map((element, index) => (
                <Fragment key={index}>
                    <Typography
                        variant="bodyRegular"
                        color={ColoursObj.font.primary}
                        component="span"
                    >
                        {element !== 'none'
                            ? getListDisplayName(element, elements) || ''
                            : 'None'}
                    </Typography>
                    {index < visibleItems.length - 1 && (
                        <Typography
                            variant="bodyRegular"
                            color={ColoursObj.font.primary}
                            component="span"
                        >
                            {', '}
                        </Typography>
                    )}
                </Fragment>
            ))}

            {overflowCount > 0 && (
                <Typography
                    variant="bodyRegular"
                    color={ColoursObj.font.primary}
                    component="span"
                >
                    {`+${overflowCount}`}
                </Typography>
            )}
        </Stack>
    )
}

function profileMainFieldFormatter(field: any, lang?: string) {
    switch (field.type) {
        case 'text':
            return (
                <Typography
                    variant="bodyRegular"
                    color={ColoursObj.font.primary}
                >
                    {field?.value || ''}
                </Typography>
            )
        case 'multiLineText':
            return (
                <Typography
                    variant="bodyRegular"
                    color={ColoursObj.font.primary}
                >
                    {field?.value?.length > 30
                        ? `${field.value.substring(0, 30)}...`
                        : field?.value || ''}
                </Typography>
            )
        case 'list':
            return <ListDisplay addInfo={field.value} elements={field.list} />
        case 'countrySelect':
            return (
                <Typography
                    variant="bodyRegular"
                    color={ColoursObj.font.primary}
                >
                    <>
                        {getFlagEmojiFromCountryCode(field?.value || '')}
                        {
                            CountryCodesWithNames.find((countryObj) => {
                                return countryObj.id === field?.value
                            })?.displayName
                        }
                    </>
                </Typography>
            )
        case 'dateSelect':
            const date = new EventrDate(
                field?.value
            ).toItinLocalStyledShortDateString(lang || 'en') //the function calling this should always call from language context which will always give a defualt so should not occour if it does has a defualt now.
            return (
                <Typography
                    variant="bodyRegular"
                    color={ColoursObj.font.primary}
                >
                    {date || ''}
                </Typography>
            )
        default:
            return null
    }
}
